import l10n from '../../lang/locallang';

/**
 * Class representing popup functionalities.
 * Provides methods to open, close popups and handle related events.
 */
export default class PopupClass {
    /**
     * Constructs the popup class and initializes event listeners and scroll width.
     */
    constructor() {
        this.openedPopups = []; // Stack to keep track of opened popups
        this.initializeEventListeners(); // Initializes all event listeners.
        this.calculateScrollWidth(); // Calculates and sets the scroll width.

        // Create popup if it absent and it's gallery
        const galleryPopup = document.querySelector('.js-gallery-popup');
        const galleryLinks = document.querySelectorAll('[data-gallery]');

        if (!galleryPopup && galleryLinks) {
            this.createGalleryPopupMarkup();
        }
    }

    /**
     * Initializes all necessary event listeners for popup functionality.
     */
    initializeEventListeners() {
        this.closeOnEsc(); // Sets up listener for closing pop-up on ESC key press.
        this.openOnLink(); // Sets up listener for opening pop-up when specific links are clicked.
        this.closeOnButton(); // Sets up listener for closing pop-up when close buttons are clicked.
        this.closeOnBackground(); // Sets up listener for closing pop-up when clicking outside the pop-up.
        this.openOnGalleryLink(); // Sets up listener for opening pop-up when gallery link is clicked.
    }

    /**
     * Opens a popup.
     * @param {string} href - The ID of the popup element to open.
     */
    open(href) {
        const popup = document.getElementById(href); // Fetches the pop-up element by ID.
        if (!popup) return; // If the pop-up doesn't exist, exit the function.

        // Check if the popup is already open to prevent duplicate events
        if (popup.classList.contains('opened')) return;

        let background = this.getOrCreateBackground(); // Gets or creates the background overlay.
        background.style.visibility = 'visible'; // Makes the background visible.
        setTimeout(() => background.style.opacity = '1', 50); // Fades in the background.

        popup.classList.add('opened'); // Opens the pop-up.
        document.body.classList.add('show-popup'); // Adds a class to the body for showing the pop-up.
        this.openedPopups.push(popup); // Push the popup to the stack

        // trigger on open popup event
        const event = new CustomEvent('vsPopupBeforeOpen', {detail: {popup}});
        document.dispatchEvent(event);
    }

    /**
     * Closes a popup.
     * @param {string} [href=null] - The ID of the popup element to close, closes the currently opened popup if not specified.
     */
    close(href = null) {
        const popup = href ? document.getElementById(href) : this.openedPopups.pop(); // Pop the most recently opened popup if href is not provided
        if (!popup) return;

        setTimeout(() => {
            popup.classList.remove('opened'); // Removes 'opened' class to close the pop-up.

            const isPopupOpened = this.openedPopups.length > 0;

            if (!isPopupOpened) {
                this.hideBackground(); // Hides the background overlay.
            }

            this.resetIframe(popup); // Resets iframe within the pop-up if present.
        }, 100);

        if (document.body.classList.contains('js-gallery-popup-opened')) {
            document.body.classList.remove('js-gallery-popup-opened');
            this.hideBackground(); // Hides the background overlay.
        }

        this.triggerPopupCloseEvent(popup); // Triggers a custom event after closing the pop-up.
    }

    /**
     * Attaches click event listeners to elements that trigger opening of popups.
     */
    openOnLink() {
        document.querySelectorAll('[data-popup]').forEach(button => {
            button.addEventListener('click', e => {
                e.preventDefault();
                const href = button.getAttribute('href').substring(1);
                button.classList.contains('popup-close') || button.classList.contains('js-popup-close')
                    ? setTimeout(() => this.open(href), 500)
                    : this.open(href);
            });
        });
    }

    /**
     * Attaches click event listeners to gallery elements that trigger opening of popups.
     */
    openOnGalleryLink() {
        // Add listener on window because we need handle cloned gallery too
        window.addEventListener('click', e => {
            const galleryLink = e.target.closest('[data-gallery]');

            if (galleryLink) {
                e.preventDefault();
                const href = galleryLink.getAttribute('href');
                const hrefAbsoluteAddress = this.createAbsoluteAddress(href);
                this.updateImageInPopup(hrefAbsoluteAddress);
                this.open('gallery-popup');
                document.body.classList.add('js-gallery-popup-opened');
                this.handleGalleryImageChange(galleryLink);
            }
        });
    }

    /**
     * Handle gallery behavior (changing images).
     * @param {HTMLElement} galleryLink - Current clicked link in gallery.
     */
    handleGalleryImageChange(galleryLink) {
        const linkParentEl = galleryLink.closest('.gallery-wrapper');
        const linkId = galleryLink.closest('.gallery-item').dataset.id;
        const currentGalleryItems = [...linkParentEl.querySelectorAll('.gallery-item')];
        const visibleGalleryItems = currentGalleryItems.filter(item => window.getComputedStyle(item).display !== 'none');
        const nextBtn = document.querySelector('.js-gallery-popup-next');
        const prevBtn = document.querySelector('.js-gallery-popup-prev');
        let currentIndex = visibleGalleryItems.findIndex(element => element.dataset.id === linkId);

        if (visibleGalleryItems.length > 1) {
            [nextBtn, prevBtn].forEach(btn => btn.classList.remove('is-hidden'));
            this.hideShowGalleryButtons(prevBtn, nextBtn, currentIndex, visibleGalleryItems);

            // Because we need to remove event listener after close popup
            const prevBtnClickHandler = (e) => {
                e.stopPropagation();

                if (currentIndex > 0) {
                    currentIndex--;
                    this.handleSelectedImage(visibleGalleryItems, currentIndex);
                    this.hideShowGalleryButtons(prevBtn, nextBtn, currentIndex, visibleGalleryItems);
                }
            };

            // Because we need to remove event listener after close popup
            const nextBtnClickHandler = (e) => {
                e.stopPropagation();

                if (currentIndex < visibleGalleryItems.length - 1) {
                    currentIndex++;
                    this.handleSelectedImage(visibleGalleryItems, currentIndex);
                    this.hideShowGalleryButtons(prevBtn, nextBtn, currentIndex, visibleGalleryItems);
                }
            }

            prevBtn.addEventListener('click', prevBtnClickHandler);
            nextBtn.addEventListener('click', nextBtnClickHandler);

            document.addEventListener('vsPopupAfterClose', () => {
                prevBtn.removeEventListener('click', prevBtnClickHandler);
                nextBtn.removeEventListener('click', nextBtnClickHandler);

                // Remove popup image src after close popup for smooth animation
                document.querySelector('.vs-popup-gallery__image').src = '';
            })
        } else {
            [nextBtn, prevBtn].forEach(btn => btn.classList.add('is-hidden'));
        }
    }

    /**
     * Handle hide/show gallery buttons.
     * @param {HTMLElement} prevBtn - Previous button in gallery popup.
     * @param {HTMLElement} nextBtn - Next button in gallery popup.
     * @param {Number} currentIndex - Index of current clicked link.
     * @param {Array} currentGalleryItems - Array of items which belong to the same wrapper as current clicked link.
     */
    hideShowGalleryButtons(prevBtn, nextBtn, currentIndex, currentGalleryItems) {
        prevBtn.classList.toggle('is-hidden', currentIndex === 0);
        nextBtn.classList.toggle('is-hidden', currentIndex === currentGalleryItems.length - 1);
    }

    /**
     * Handle hide/show gallery buttons.
     * @param {Number} currentIndex - Index of current clicked link.
     * @param {Array} currentGalleryItems - Array of items which belong to the same wrapper as current clicked link.
     */
    handleSelectedImage(currentGalleryItems, currentIndex) {
        const image = currentGalleryItems[currentIndex].querySelector('img');
        const src = image.getAttribute('src');
        this.updateImageInPopup(src);
    }

    /**
     * Handle hide/show gallery buttons.
     * @param {String} smallImageSrc - Src of target image.
     */
    updateImageInPopup(smallImageSrc) {
        const largeImage = document.querySelector('.vs-popup-gallery__image');
        largeImage.classList.add('is-hidden');

        setTimeout(() => {
            largeImage.src = smallImageSrc;
            largeImage.classList.remove('is-hidden');
        }, 200);
    }

    /**
     * Handle hide/show gallery buttons.
     * @param {String} relativeAddress - Relative address of target image.
     */
    createAbsoluteAddress(relativeAddress) {
        const currentDomain = window.location.origin;
        const absoluteAddress = new URL(relativeAddress, currentDomain);
        return absoluteAddress.toString();
    }

    /**
     * Create markup for gallery popup.
     */
    createGalleryPopupMarkup() {
        const { goToNextImage, goToPreviousImage } = l10n.popupClass;

        const galleryPopupHtml = `
                <div class="vs-popup vs-popup-gallery js-gallery-popup" id="gallery-popup">
                    <div class="vs-popup-gallery__container">
                        <button type="button" class="vs-popup-gallery__close js-popup-close">
                            <i class="icon icon-close-grey"></i>
                        </button>
                        <button type="button" class="vs-popup-gallery__next js-gallery-popup-next" aria-label="${goToNextImage}">
                            <i class="icon icon-chevron-right"></i>
                        </button>
                        <button type="button" class="vs-popup-gallery__prev js-gallery-popup-prev" aria-label="${goToPreviousImage}">
                            <i class="icon icon-chevron-left"></i>
                        </button>
                        <div class="vs-popup-gallery__image-wrap">
                            <img class="vs-popup-gallery__image" src="" alt>
                        </div>
                    </div>
                </div>`
        document.body.insertAdjacentHTML('beforeend', galleryPopupHtml);
    }

    /**
     * Sets up an event listener to close the popup when the ESC key is pressed.
     */
    closeOnEsc() {
        document.addEventListener('keydown', e => {
            if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
                this.close();
            }
        });
    }

    /**
     * Adds an event listener to close the popup when clicking on the background.
     */
    closeOnBackground() {
        window.addEventListener('click', e => {
            if (e.target.classList.contains('vs-popup-background')) {
                this.close();
            }
        });
    }

    /**
     * Adds an event listener to close the popup when clicking on designated close buttons.
     */
    closeOnButton() {
        window.addEventListener('click', e => {
            if (e.target.closest('.js-popup-close') && e.target.closest('.vs-popup') && e.target.closest('.vs-popup').getAttribute('id') === 'gallery-popup') {
                this.close('gallery-popup');
            } else if (e.target.closest('.popup-close') || e.target.closest('.js-popup-close')) {
                this.close();
            }
        });
    }

    /**
     * Calculates and sets the scrollbar width as a CSS variable.
     */
    calculateScrollWidth() {
        const scrollWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollWidth}px`);
    }

    /**
     * Retrieves or creates the background overlay element for the popup.
     * @returns {HTMLElement} The background overlay element.
     */
    getOrCreateBackground() {
        let background = document.querySelector('.vs-popup-background');
        if (!background) {
            background = document.createElement('div');
            document.body.insertBefore(background, null);
            background.classList.add('vs-popup-background');
        }
        return background;
    }

    /**
     * Hides the background overlay for the popup.
     */
    hideBackground() {
        const background = document.querySelector('.vs-popup-background');
        if (background) {
            background.style.visibility = 'hidden';
            background.style.opacity = '0';
            document.body.classList.remove('show-popup');
        }
    }

    /**
     * Resets the src attribute of an iframe inside the popup, if it exists.
     * @param {HTMLElement} popup - The popup element containing the iframe.
     */
    resetIframe(popup) {
        const iframe = popup.querySelector('iframe');
        if (iframe && popup.id !== 'payment_2-popup') {
            iframe.setAttribute('src', '');
        }
    }

    /**
     * Triggers a custom event after a popup is closed.
     * @param {HTMLElement} popup - The popup element that was closed.
     */
    triggerPopupCloseEvent(popup) {
        const event = new CustomEvent('vsPopupAfterClose', {detail: {popup}});
        document.dispatchEvent(event);
        // Preloader.stop(); // Uncomment if Preloader is defined
    }

    closeAllPopups() {
        const popups = document.querySelectorAll('.vs-popup');
        popups.forEach(popup => {
            this.close(popup.id);
        });
    }
}

window.vsPopupClass = PopupClass; // Exposes the class globally.
